import { FormEvent, useEffect, useState } from 'react'
import { LoaderFunctionArgs, useLoaderData, useNavigate, useParams } from 'react-router-dom'
import { useCurrentHealthCenter } from '../../components/AppShell'
import { TreatmentTemplate, TreatmentTemplateAttributes, createTreatmentTemplate, fetchTreatmentTemplate, updateTreatmentTemplate } from '../../api/treatments'
import { ApiError } from '../../lib/api'
import { Button, CurrencyField, Panel, PanelBody, PanelHeading, RadioGroup, TextAreaField, TextField } from '../../components/common'
import { trackEvent } from '../../lib/config'
import { formatMoney } from '../../lib/currency'

type FormValues = {
  name: string
  code: string
  price: string
  description: string
  note: string
  timeless: boolean
}

const EMPTY_FORM: FormValues = { name: '', code: '', price: '', description: '', note: '', timeless: true }

function autosaveKey(): string {
  return `hc-autosave:${window.location.pathname}`
}

function getFormFromTemplate(template: TreatmentTemplate): FormValues {
  return {
    name: template.name ?? '',
    code: template.code ?? '',
    price: formatMoney(template.price ?? ''),
    description: template.description ?? '',
    note: template.note ?? '',
    timeless: template.timeless === true,
  }
}

function restoreDraft(): FormValues {
  const stored = window.localStorage.getItem(autosaveKey())
  return stored ? { ...EMPTY_FORM, ...(JSON.parse(stored) as Partial<FormValues>) } : EMPTY_FORM
}

export function loader({ params }: LoaderFunctionArgs): Promise<TreatmentTemplate> {
  return fetchTreatmentTemplate(Number(params.healthCenterId), params.templateId as string)
}

export default function TreatmentTemplateFormPage() {
  const healthCenter = useCurrentHealthCenter()
  const navigate = useNavigate()
  const params = useParams()
  const templateId = params.templateId
  const template = useLoaderData() as TreatmentTemplate | undefined
  const [values, setValues] = useState<FormValues>(() => (template ? getFormFromTemplate(template) : restoreDraft()))
  const [errors, setErrors] = useState<readonly string[]>([])

  useEffect(() => {
    if (!templateId) {
      window.localStorage.setItem(autosaveKey(), JSON.stringify(values))
    }
  }, [templateId, values])

  if (!healthCenter) {
    return null
  }

  const set = (field: keyof FormValues, value: string | boolean) => setValues({ ...values, [field]: value })

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const attributes: TreatmentTemplateAttributes = {
      name: values.name,
      code: values.code,
      price: values.price,
      description: values.description,
      note: values.note,
      timeless: values.timeless,
    }
    try {
      if (templateId) {
        await updateTreatmentTemplate(healthCenter.id, templateId, attributes)
      } else {
        await createTreatmentTemplate(healthCenter.id, attributes)
        window.localStorage.removeItem(autosaveKey())
      }
      trackEvent(templateId ? 'Treatment Template Updated' : 'Treatment Template Created')
      navigate(`/health_centers/${healthCenter.id}/treatment_templates`)
    } catch (error) {
      if (error instanceof ApiError && error.body && typeof error.body === 'object') {
        trackEvent('Form Save Failed', { form: 'treatment_template', reason: 'validation', status_code: error.status, fields: Object.keys(error.body) })
        setErrors(Object.entries(error.body as Record<string, string[]>).map(([field, messages]) => `${field} ${messages.join(', ')}`))
        return
      }
      throw error
    }
  }

  return (
    <Panel>
      <PanelHeading>
        <h1>{templateId ? 'Modificando Tratamiento' : 'Nuevo Tratamiento'}</h1>
      </PanelHeading>
      <PanelBody className="container">
        <form onSubmit={(event) => void handleSubmit(event)}>
          {errors.length > 0 && (
            <div id="error_explanation">
              <ul>
                {errors.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </div>
          )}

          <TextField label="Nombre" labelStyle="plain" value={values.name} onChange={(value) => set('name', value)} />
          <TextField label="Codigo" labelStyle="plain" value={values.code} onChange={(value) => set('code', value)} />
          <RadioGroup
            className="col-sm-12 col-md6"
            name="timeless"
            inline={false}
            value={values.timeless ? 'true' : 'false'}
            onChange={(value) => set('timeless', value === 'true')}
            options={[
              { value: 'true', label: 'Precio por sesión (duración indefinida)' },
              { value: 'false', label: 'Precio total del tratamiento (duración fija)' },
            ]}
          />
          <CurrencyField label="Precio" value={values.price} onChange={(value) => set('price', value)} />
          <TextAreaField
            label="Descripción"
            labelStyle="plain"
            rows={8}
            value={values.description}
            onChange={(value) => set('description', value)}
          />
          <TextAreaField label="Nota" labelStyle="plain" rows={6} value={values.note} onChange={(value) => set('note', value)} />
          <div className="actions">
            <Button type="submit">Crear Tratamiento</Button>
          </div>
        </form>
      </PanelBody>
    </Panel>
  )
}
